// @ts-check
import { objectEntries } from '@erpagroapp/common';
import {
  ConfigurationParameterReturnType,
  ConfigurationParameterSource,
} from './enums';
import { convertParamValueToBoolean } from './helpers';
import {
  paramValues,
  saveValuesToLocalStorage,
} from './paramValues';
import { params } from './storeParams';

/**
 * @template {import('./storeParams').ParameterKeys} K
 * @param {K} name
 * @returns {import('./paramValues').ParamValues[K]}
 */
export function useParamRef(name) {
  if (!Object.hasOwn(paramValues, name)) {
    throw new Error(`Configuration parameter '${name}' is not registered`);
  }

  return paramValues[name];
}

export function useParamValues() {
  return paramValues;
}

/**
 * Loads the configuration parameters and saves them to local storage.
 * @param {number[]} availableModuleIds - An array of available module IDs.
 * @param {number[]} availableProcessIds - An array of available process IDs.
 * @param {import('./types').ConfigurationParametersFetchFunction} fetchParams - A function that fetches the configuration parameters.
 * @returns {Promise<void>}
 */
export async function initConfigurationParams(availableModuleIds, availableProcessIds, fetchParams) {
  await startLoadingParams(availableModuleIds, availableProcessIds, fetchParams);
  saveValuesToLocalStorage();
}

/**
 *
 * @param {number[]} availableModuleIds
 * @param {number[]} availableProcessIds
 * @param {import('./types').ConfigurationParametersFetchFunction} fetchParams
 * @returns {Promise<void>}
 */
async function startLoadingParams(availableModuleIds, availableProcessIds, fetchParams) {
  if (Object.keys(params).length === 0) {
    return;
  }

  /** @type {import('./types').ConfigurationParameterInstance} */
  const loadedParameters = {};

  for (const [key, parameter] of objectEntries(params)) {
    if (shouldLoadParam(parameter, availableModuleIds, availableProcessIds)) {
      loadedParameters[key] = parameter;
    } else {
      const paramRef = useParamRef(key);
      paramRef.value = parameter.defaultValue;
    }
  }

  if (Object.keys(loadedParameters).length > 0) {
    await loadMultipleConfigurationParams(loadedParameters, fetchParams);
  }
}

/**
 * @param {import('./types').ConfigurationParameterProps} parameter
 * @param {number[]} availableModuleIds
 * @param {number[]} availableProcessIds
 * @returns {boolean}
 */
function shouldLoadParam(parameter, availableModuleIds, availableProcessIds) {
  if (!parameter.loadingRules) {
    return true;
  }

  const { requiredModuleIds, requiredProcessIds } = parameter.loadingRules;

  if (Array.isArray(requiredModuleIds) && requiredModuleIds.length > 0) {
    const hasAvailableModules = Array.isArray(availableModuleIds) && availableModuleIds.length > 0;
    if (!hasAvailableModules) {
      return false;
    }

    const found = requiredModuleIds.some((id) => availableModuleIds.includes(id));
    if (!found) {
      return false;
    }
  }

  if (Array.isArray(requiredProcessIds) && requiredProcessIds.length > 0) {
    const hasAvailableProcesses = Array.isArray(availableProcessIds) && availableProcessIds.length > 0;
    if (!hasAvailableProcesses) {
      return false;
    }

    const found = requiredProcessIds.some((id) => availableProcessIds.includes(id));
    if (!found) {
      return false;
    }
  }

  return true;
}

/**
 * @param {import('./types').ConfigurationParameterInstance} configParameters
 * @param {import('./types').ConfigurationParametersFetchFunction} fetchParams
 * @returns {Promise<void>}
 */
async function loadMultipleConfigurationParams(configParameters, fetchParams) {
  const requestPayload = Object.entries(configParameters).map(([name, config]) => {
    /** @type {Partial<import('@erpagroapp/types').ParametroConfiguracion>} */
    const payload = {
      Codigo: name,
      LugarDefinicionParametro: /** @type {never} */ (config.source),
    };

    if (config.source === ConfigurationParameterSource.ERP) {
      payload.Componente = config.component;
      payload.CodigoCasoUso = config.useCaseCode;
    }

    payload.NombreEntidad = config.entityModel;
    payload.CampoBusqueda = config.searchField;

    return payload;
  });

  const { data } = await fetchParams(requestPayload);

  const validatedData = Array.isArray(data) && data.length > 0
    ? data
    : [];

  if (validatedData.length > 0) {
    for (const [name, paramProps] of Object.entries(configParameters)) {
      let value = paramProps.defaultValue;

      const responseData = validatedData.find((item) => item.Codigo === name);

      if (responseData?.Valor !== undefined && responseData.Valor !== null) {
        switch (paramProps.returnType) {
          case ConfigurationParameterReturnType.NUMBER:
            if (typeof responseData.Valor === 'number') {
              value = responseData.Valor;
            } else if (typeof responseData.Valor === 'string') {
              value = parseInt(responseData.Valor, 10);
            }
            break;
          case ConfigurationParameterReturnType.BOOLEAN:
            if (typeof responseData.Valor === 'boolean') {
              value = responseData.Valor;
            } else if (typeof responseData.Valor === 'string') {
              value = convertParamValueToBoolean(responseData.Valor);
            }
            break;
          case ConfigurationParameterReturnType.JSON:
            if (typeof responseData.Valor === 'string') {
              value = /** @type {string} */ (JSON.parse(responseData.Valor));
            }
            break;
          case ConfigurationParameterReturnType.OBJECT:
          case ConfigurationParameterReturnType.STRING:
            value = /** @type {never} */ (responseData.Valor);
            break;
          default:
            // eslint-disable-next-line no-console
            console.error(`Invalid type for parameter "${name}".`);
        }
      }

      const key = /** @type {import('./storeParams').ParameterKeys} */ (name);
      paramValues[key].value = value;
    }
  }
}
